import { IframeConfig, RetryConfigCOM, RetryConfigES, SpinnerConfig, WidgetConfig, WidgetType } from "../common/config";
import { classByDevice, getIframeOrigin, getIframeUrl, getLocale } from "../common/utils";
import { EventType } from "../common/types";
import { logger } from "./logger";
import { IframeEvents } from "../common/events";

const VALIDATION_RULES = {
  isValidData: (data: any) => {
    const isValidData = data?.data && typeof data === "object";
    return isValidData;
  },
  isNotReactDevTools: (source: any) => {
    return !(typeof source === "string" && /^react-devtools/i.test(source));
  },
  hasValidSource: (source: any, widgetType: WidgetType) => {
    const hasValidSource = source === IframeConfig[widgetType].SOURCE_ID;
    return hasValidSource;
  },
  hasValidOrigin: (origin: string, widgetType: WidgetType) => {
    try {
      const originValid = new URL(origin).origin === getIframeOrigin(widgetType);
      return originValid;
    } catch {
      return false;
    }
  },
} as const;

const getRetryConfig = () => (getLocale() === WidgetConfig.LOCALE_ES ? RetryConfigES : RetryConfigCOM);

export type IframeCallback<Payload = unknown> = (payload: Payload) => void;

export class Iframe {
  private iframe: HTMLIFrameElement;
  private iframeWindow: Window;
  private subscriptions: Map<string, IframeCallback> = new Map();
  private timeoutId: number | null = null;
  private widgetType: WidgetType;
  private widgetId: string;
  private selector: string;

  constructor() {}

  // DOM
  private getWidgetWrapper(wrapperId: string): HTMLElement {
    const widgetWrapper = document.getElementById(wrapperId);
    if (!widgetWrapper) throw new Error(`Failed to match: ${wrapperId}`);
    return widgetWrapper;
  }

  private createIframeWrapper(wrapperId: string, headerDisabled: string) {
    const widgetWrapper = this.getWidgetWrapper(wrapperId);

    const iframeWrapper = document.createElement("div");
    iframeWrapper.id = this.widgetId;
    iframeWrapper.classList.add(WidgetConfig.CSS_CLASS);

    if (!headerDisabled) {
      const headerText = document.createElement("h3");
      headerText.classList.add(IframeConfig.HEADER_CSS_CLASS);
      headerText.textContent = IframeConfig[this.widgetType].HEADER_TITLE;
      iframeWrapper.prepend(headerText);
    }

    widgetWrapper.appendChild(iframeWrapper);
    widgetWrapper.classList.add(classByDevice());

    return iframeWrapper;
  }

  private createIframe(iframeUrl: string): HTMLIFrameElement {
    const iframe = document.createElement("iframe");
    iframe.id = `${IframeConfig.ID_PREFIX}-${this.widgetType}`;
    iframe.referrerPolicy = "origin";
    iframe.src = iframeUrl;
    iframe.classList.add(IframeConfig.CSS_CLASS);
    return iframe;
  }

  private createSpinner(iframeWrapper: HTMLElement): void {
    const spinnerId = `${SpinnerConfig.ID}-${this.widgetType}`;
    const activeSpinner = document.getElementById(spinnerId);

    if (iframeWrapper.contains(activeSpinner)) {
      return;
    }

    const spinner = document.createElement("div");
    spinner.id = spinnerId;
    spinner.className = SpinnerConfig.ID;

    const spinnerWrapper = document.createElement("div");
    spinnerWrapper.id = `${SpinnerConfig.WRAPPER_ID}-${this.widgetType}`;
    spinnerWrapper.className = SpinnerConfig.WRAPPER_ID;
    spinnerWrapper.appendChild(spinner);

    iframeWrapper.appendChild(spinnerWrapper);
  }

  private init(wrapperId: string, headerDisabled: string): HTMLIFrameElement {
    const iframeWrapper = this.createIframeWrapper(wrapperId, headerDisabled);
    this.createSpinner(iframeWrapper);
    const iframe = this.createIframe(getIframeUrl(this.widgetType));
    iframeWrapper.appendChild(iframe);
    return iframe;
  }

  private closeIframe(): void {
    this.removeSpinner();
    const iframeWrapperElem = document.getElementById(this.widgetId);
    if (iframeWrapperElem) iframeWrapperElem.parentNode.removeChild(iframeWrapperElem);
  }

  // Communication
  private validateEvent({ data, origin }: EventType): boolean {
    if (!origin || !VALIDATION_RULES.isValidData(data)) return false;

    const { source } = data.data;

    return [
      VALIDATION_RULES.isNotReactDevTools(source),
      VALIDATION_RULES.hasValidSource(source, this.widgetType),
      VALIDATION_RULES.hasValidOrigin(origin, this.widgetType),
    ].every(Boolean);
  }

  private postMessageListener = (event: MessageEvent) => {
    if (!this.validateEvent(event)) {
      return;
    }
    const { action, payload } = event.data.data;
    if (this.subscriptions.has(action)) {
      logger.log(`[${Iframe.name}] Received message: `, {
        action,
        payload,
      });
      this.subscriptions.get(action)(payload);
    }
  };

  private listener = () => {
    window.addEventListener(IframeConfig.MESSAGE_EVENT_TYPE, this.postMessageListener, false);
  };

  private openMessageListener = (event: EventType): void => {
    if (!this.validateEvent(event)) return;

    if (event.data.data.action !== IframeEvents.OPEN) {
      return;
    }

    window.addEventListener(IframeConfig.MESSAGE_EVENT_TYPE, this.postMessageListener, false);

    clearTimeout(this.timeoutId);
  };

  public load = (selector: string, headerDisabled: string, widgetType: WidgetType) => {
    this.selector = selector;
    this.widgetType = widgetType;
    this.widgetId = `${WidgetConfig.ID_PREFIX}-${widgetType}`;
    this.iframe = this.init(selector, headerDisabled);
    this.iframeWindow = this.iframe.contentWindow;
    this.timeoutId = window.setTimeout(() => {
      this.showRetryButton();
    }, IframeConfig.TIMESTAMP);
    window.addEventListener(IframeConfig.MESSAGE_EVENT_TYPE, this.openMessageListener, false);
    this.listener();
  };

  public removeSpinner(): void {
    const spinnerWrapperElem = document.getElementById(`${SpinnerConfig.WRAPPER_ID}-${this.widgetType}`);
    const iframeWrapper = document.getElementById(this.widgetId);
    const iframeElem = document.getElementById(`${IframeConfig.ID_PREFIX}-${this.widgetType}`);

    if (iframeWrapper) iframeWrapper.style.display = "flex";
    if (iframeElem) iframeElem.style.display = "";
    if (spinnerWrapperElem) {
      spinnerWrapperElem.remove();
    }
  }

  public publish<Payload = unknown>(topic: string, payload: Payload) {
    logger.log(`[${Iframe.name}] Publishing message: `, {
      topic,
      payload,
    });
    this.iframeWindow.postMessage(
      {
        source: IframeConfig.SOURCE,
        action: topic,
        payload,
      },
      getIframeOrigin(this.widgetType),
    );
  }

  public subscribe(topic: string, callback: IframeCallback) {
    this.subscriptions.set(topic, callback);
  }

  private showRetryButton(): void {
    const existingIframe = document.getElementById(`${IframeConfig.ID_PREFIX}-${this.widgetType}`);
    if (existingIframe) existingIframe.remove();
    this.removeSpinner();

    window.removeEventListener(IframeConfig.MESSAGE_EVENT_TYPE, this.openMessageListener, false);
    window.removeEventListener(IframeConfig.MESSAGE_EVENT_TYPE, this.postMessageListener, false);

    const iframeWrapper = document.getElementById(`${WidgetConfig.ID_PREFIX}-${this.widgetType}`);
    if (!iframeWrapper) {
      logger.log(`[${Iframe.name}] Wrapper not found, full dispose`);
      this.dispose();
      return;
    }

    this.removeRetryButton();

    const config = getRetryConfig();
    const retryContainer = document.createElement("div");
    retryContainer.id = `${config.CONTAINER_ID}-${this.widgetType}`;
    retryContainer.classList.add("iframe-retry-container");

    const titleError = document.createElement("span");
    const message = document.createElement("p");

    titleError.classList.add("iframe-retry-title");
    message.classList.add("iframe-retry-message");
    titleError.textContent = config.ERROR_TITLE;
    message.textContent = config.ERROR_MESSAGE;

    const button = document.createElement("button");
    button.classList.add("iframe-retry-button");
    button.textContent = config.BUTTON_TEXT;
    button.addEventListener("click", () => this.reloadIframe());

    retryContainer.appendChild(titleError);
    retryContainer.appendChild(message);
    retryContainer.appendChild(button);
    iframeWrapper.appendChild(retryContainer);
  }

  private removeRetryButton(): void {
    const retryContainer = document.getElementById(`${getRetryConfig().CONTAINER_ID}-${this.widgetType}`);
    if (retryContainer) retryContainer.remove();
  }

  private reloadIframe(): void {
    this.removeRetryButton();

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    window.removeEventListener(IframeConfig.MESSAGE_EVENT_TYPE, this.openMessageListener, false);
    window.removeEventListener(IframeConfig.MESSAGE_EVENT_TYPE, this.postMessageListener, false);

    const existingIframe = document.getElementById(`${IframeConfig.ID_PREFIX}-${this.widgetType}`);
    if (existingIframe) existingIframe.remove();
    this.removeSpinner();

    const iframeWrapper = document.getElementById(`${WidgetConfig.ID_PREFIX}-${this.widgetType}`);
    if (!iframeWrapper) {
      this.dispose();
      return;
    }

    this.createSpinner(iframeWrapper);

    const newIframe = this.createIframe(getIframeUrl(this.widgetType));
    iframeWrapper.appendChild(newIframe);
    this.iframe = newIframe;
    this.iframeWindow = newIframe.contentWindow;

    this.timeoutId = window.setTimeout(() => {
      this.showRetryButton();
    }, IframeConfig.TIMESTAMP);

    window.addEventListener(IframeConfig.MESSAGE_EVENT_TYPE, this.openMessageListener, false);
    this.listener();
  }

  public dispose() {
    window.removeEventListener(IframeConfig.MESSAGE_EVENT_TYPE, this.openMessageListener, false);
    window.removeEventListener(IframeConfig.MESSAGE_EVENT_TYPE, this.postMessageListener, false);
    this.removeRetryButton();
    this.closeIframe();
    this.subscriptions.clear();
    logger.log(`[${Iframe.name}] Disposed`);
  }

  public getWidgetType(): WidgetType {
    return this.widgetType;
  }

  public getSelector(): string {
    return this.selector;
  }
}
